// Components
import { Select, Textarea, Stack, Skeleton } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormActions } from "@benchmark/ui/components/atomic";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { useCallback, useEffect, useRef } from "react";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Types
import type { Goal, Patient } from "@medplum/fhirtypes";
import type { GoalFormData } from "@benchmark/core/types";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createGoalFacade } from "@/facades/goal.facade";
import { goalToForm } from "@benchmark/core/adapters";
import { GOAL_CATEGORIES } from "@benchmark/core/constants";
import { getDateOnly } from "@benchmark/ui/utils";

/**
 * Props for the GoalForm component.
 * @agentic connections
 */
export type GoalFormProps = {
  patientId: string;
  goal?: Goal;
  onSuccess: (goalId: string) => void;
  onCancel: () => void;
};

/**
 * Form for creating and editing patient goals. Follows FHIR R4 compliance.
 * @agentic connections
 */
export const GoalForm = ({
  patientId,
  goal,
  onSuccess,
  onCancel,
}: GoalFormProps) => {
  const medplum = useMedplum();
  const goalFacade = createGoalFacade(medplum);
  const { data: patientResource } = usePatientResource(patientId);
  const patient = patientResource as Patient | undefined;
  const { practitionerRole } = useCurrentPractitionerRole();

  // Helper to convert Date to YYYY-MM-DD string
  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const form = useForm<GoalFormData>({
    initialValues: {
      category: "",
      description: "",
      notes: "",
      lifecycleStatus: "active",
      startDate: dateToString(getDateOnly(new Date())) || undefined,
    },
    validate: {
      category: (value) => (!value ? "Category is required" : null),
      description: (value) => (!value ? "Description is required" : null),
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
  });

  // Track if we've loaded the goal data to prevent overwriting user edits
  const goalIdRef = useRef<string | undefined>(goal?.id);
  const hasLoadedGoalData = useRef(false);

  // Load goal data only once when goal changes
  useEffect(() => {
    // Only load if we have a goal and haven't loaded this goal yet
    if (goal && goal.id && goal.id !== goalIdRef.current) {
      goalIdRef.current = goal.id;
      hasLoadedGoalData.current = false;
    }

    if (goal && !hasLoadedGoalData.current) {
      const existingFormData = goalToForm(goal);
      hasLoadedGoalData.current = true;
      form.setValues({
        category: existingFormData.category || "",
        description: existingFormData.description || "",
        notes: existingFormData.notes || "",
        lifecycleStatus: existingFormData.lifecycleStatus || "active",
        startDate:
          existingFormData.startDate ||
          dateToString(getDateOnly(new Date())) ||
          undefined,
        ...(existingFormData.achievementStatus && {
          achievementStatus: existingFormData.achievementStatus,
        }),
        ...(existingFormData.statusDate && {
          statusDate: existingFormData.statusDate,
        }),
      });
      form.resetDirty();
    } else if (!goal) {
      // Reset when goal is cleared (switching to create mode)
      goalIdRef.current = undefined;
      hasLoadedGoalData.current = false;
      form.setValues({
        category: "",
        description: "",
        notes: "",
        lifecycleStatus: "active",
        startDate: dateToString(getDateOnly(new Date())) || undefined,
      });
      form.resetDirty();
    }
    // Only depend on goal?.id - form.setValues is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.id]);

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const handleSubmit = useCallback(
    (formData: GoalFormData) =>
      createSubmissionHandler(async (fd: GoalFormData) => {
        if (!patient || !practitionerRole) {
          showErrorNotification("Missing patient or practitioner information.");
          return;
        }

        try {
          if (goal && goal.id) {
            // Update existing goal
            const updated = await goalFacade.update(
              fd,
              goal,
              patient,
              practitionerRole,
            );
            showSuccessNotification("Goal updated successfully.");
            markAsClean();
            if (updated.id) {
              onSuccess(updated.id);
            }
          } else {
            // Create new goal
            const created = await goalFacade.create(
              fd,
              patient,
              practitionerRole,
            );
            showSuccessNotification("Goal created successfully.");
            markAsClean();
            if (created.id) {
              onSuccess(created.id);
            }
          }
        } catch (error) {
          showErrorNotification(error, {
            title: goal ? "Unable to Update Goal" : "Unable to Create Goal",
          });
        }
      })(formData),
    [
      patient,
      practitionerRole,
      goalFacade,
      goal,
      onSuccess,
      markAsClean,
      createSubmissionHandler,
    ],
  );

  if (!patient || !practitionerRole) {
    return <Skeleton height={200} />;
  }

  // Prepare category options
  const categoryOptions = GOAL_CATEGORIES.map((cat) => ({
    value: cat.code,
    label: cat.display,
  }));

  const isEditing = !!goal;

  return (
    <form
      aria-label={isEditing ? "Edit goal form" : "Create goal form"}
      onSubmit={form.onSubmit(handleSubmit, (_validationErrors) => {
        showErrorNotification("Please fix the form errors before submitting.");
      })}
    >
      <Stack gap="md">
        <FormField
          label="Goal Category"
          required
          aria-label="Select goal category"
        >
          <Select
            placeholder="Select a category"
            data={categoryOptions}
            {...form.getInputProps("category")}
            searchable
            clearable={false}
            aria-label="Select goal category"
          />
        </FormField>

        <FormField
          label="Goal Description"
          required
          aria-label="Goal description"
        >
          <Textarea
            placeholder="Describe the goal..."
            value={form.values.description}
            onChange={(event) => {
              form.setFieldValue("description", event.currentTarget.value);
            }}
            minRows={3}
            aria-label="Goal description"
          />
        </FormField>

        <FormField label="Notes" aria-label="Goal notes">
          <Textarea
            placeholder="Add any additional notes about this goal..."
            {...form.getInputProps("notes")}
            minRows={2}
            aria-label="Goal notes"
          />
        </FormField>

        {isEditing && (
          <FormField label="Status" aria-label="Goal status">
            <Select
              data={[
                { value: "proposed", label: "Proposed" },
                { value: "planned", label: "Planned" },
                { value: "accepted", label: "Accepted" },
                { value: "active", label: "Active" },
                { value: "on-hold", label: "On Hold" },
                { value: "completed", label: "Completed" },
                { value: "cancelled", label: "Cancelled" },
              ]}
              value={form.values.lifecycleStatus}
              onChange={(value) => {
                form.setFieldValue(
                  "lifecycleStatus",
                  (value || "active") as GoalFormData["lifecycleStatus"],
                );
              }}
              aria-label="Goal status"
            />
          </FormField>
        )}

        <FormActions
          isSubmitting={isSubmitting}
          resourceType="Goal"
          isEditing={isEditing}
          showCancel={true}
          onCancel={onCancel}
        />
      </Stack>
      <ConfirmationModal />
    </form>
  );
};
